import {
  Drawer,
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  IconButton,
  Badge,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DashboardIcon from "@mui/icons-material/Dashboard";
import SchoolIcon from "@mui/icons-material/School";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import MicIcon from "@mui/icons-material/Mic";
import AssessmentIcon from "@mui/icons-material/Assessment";
import GroupsIcon from "@mui/icons-material/Groups";
import PersonIcon from "@mui/icons-material/Person";
import ExtensionIcon from "@mui/icons-material/Extension";
import FolderIcon from "@mui/icons-material/Folder";
import SettingsIcon from "@mui/icons-material/Settings";
import PeopleIcon from "@mui/icons-material/People";
import FlagIcon from "@mui/icons-material/Flag";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { UserRole } from "@/api/generated.schemas";
import { useGetPendingApprovals } from "@/api/generated";

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactElement;
  requiredRoles?: UserRole[];
}

const mainNavItems: NavItem[] = [
  {
    label: "Dashboard",
    path: "/dashboard",
    icon: <DashboardIcon />,
  },
  {
    label: "Learning Journeys",
    path: "/pathways",
    icon: <SchoolIcon />,
  },
  {
    label: "Goals",
    path: "/goals",
    icon: <FlagIcon />,
  },
  {
    label: "AI Coach",
    path: "/ai-coach",
    icon: <SmartToyIcon />,
  },
  {
    label: "My Calls",
    path: "/transcripts",
    icon: <MicIcon />,
  },
  {
    label: "Community",
    path: "/community",
    icon: <GroupsIcon />,
  },
  {
    label: "Reports",
    path: "/reports",
    icon: <AssessmentIcon />,
    requiredRoles: [UserRole.manager, UserRole.director, UserRole.administrator],
  },
  {
    label: "Pending Approvals",
    path: "/goals/approvals",
    icon: <FactCheckIcon />,
    requiredRoles: [UserRole.manager, UserRole.director, UserRole.administrator],
  },
];

const secondaryNavItems: NavItem[] = [
  {
    label: "Profile",
    path: "/profile",
    icon: <PersonIcon />,
  },
  {
    label: "Integrations",
    path: "/integrations",
    icon: <ExtensionIcon />,
  },
  {
    label: "Resources",
    path: "/resources",
    icon: <FolderIcon />,
  },
  {
    label: "Team Management",
    path: "/admin/team-management",
    icon: <PeopleIcon />,
    requiredRoles: [UserRole.administrator],
  },
  {
    label: "Admin Settings",
    path: "/admin/settings",
    icon: <SettingsIcon />,
    requiredRoles: [UserRole.administrator],
  },
];

interface MobileDrawerProps {
  open: boolean;
  onClose: () => void;
}

export function MobileDrawer({ open, onClose }: MobileDrawerProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasRole, user } = useAuth();

  // Fetch pending approvals count for managers+
  const isManager = hasRole([UserRole.manager, UserRole.director, UserRole.administrator]);
  const { data: pendingApprovals } = useGetPendingApprovals({
    query: {
      enabled: isManager,
      staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
      refetchOnWindowFocus: true, // Refetch when tab gains focus
    },
  });
  const pendingCount = pendingApprovals
    ? pendingApprovals.creation_count + pendingApprovals.completion_count
    : 0;

  const visibleMainItems = mainNavItems.filter((item) => {
    if (!item.requiredRoles) return true;
    return hasRole(item.requiredRoles);
  });

  const visibleSecondaryItems = secondaryNavItems.filter((item) => {
    if (!item.requiredRoles) return true;
    return hasRole(item.requiredRoles);
  });

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
  };

  const getIcon = (item: NavItem) => {
    if (item.path === "/goals/approvals" && pendingCount > 0) {
      return (
        <Badge badgeContent={pendingCount} color="error" max={99}>
          {item.icon}
        </Badge>
      );
    }
    return item.icon;
  };

  const displayName = user?.display_name || user?.email || "User";

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: { width: 280 },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            {displayName}
          </Typography>
          {user?.job_title && (
            <Typography variant="caption" color="text.secondary">
              {user.job_title}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} aria-label="Close menu" edge="end">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ py: 1 }}>
        <Typography variant="overline" sx={{ px: 2, color: "text.secondary", fontWeight: 600 }}>
          Main Menu
        </Typography>
        <List disablePadding>
          {visibleMainItems.map((item) => (
            <ListItemButton
              key={item.path}
              selected={location.pathname === item.path}
              onClick={() => handleNavigation(item.path)}
              sx={{
                "&.Mui-selected": {
                  bgcolor: "primary.lighter",
                  borderRight: "3px solid",
                  borderColor: "primary.main",
                  "& .MuiListItemIcon-root": {
                    color: "primary.main",
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{getIcon(item)}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          ))}
        </List>
      </Box>

      <Divider />

      <Box sx={{ py: 1 }}>
        <Typography variant="overline" sx={{ px: 2, color: "text.secondary", fontWeight: 600 }}>
          Account
        </Typography>
        <List disablePadding>
          {visibleSecondaryItems.map((item) => (
            <ListItemButton
              key={item.path}
              selected={location.pathname === item.path}
              onClick={() => handleNavigation(item.path)}
              sx={{
                "&.Mui-selected": {
                  bgcolor: "primary.lighter",
                  borderRight: "3px solid",
                  borderColor: "primary.main",
                  "& .MuiListItemIcon-root": {
                    color: "primary.main",
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{getIcon(item)}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          ))}
        </List>
      </Box>

      <Divider />

      <Box sx={{ py: 1 }}>
        <List disablePadding>
          <ListItemButton
            selected={location.pathname === "/about"}
            onClick={() => handleNavigation("/about")}
            sx={{
              "&.Mui-selected": {
                bgcolor: "primary.lighter",
                borderRight: "3px solid",
                borderColor: "primary.main",
                "& .MuiListItemIcon-root": {
                  color: "primary.main",
                },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <InfoOutlinedIcon />
            </ListItemIcon>
            <ListItemText primary="About" />
          </ListItemButton>
        </List>
      </Box>
    </Drawer>
  );
}
